import {
  AdditionalCompensationsContractsHistoryFragment,
  AmountStrategyHistoryFragment,
  ContractTaxonomyFragment,
  ContractsAnnualWorkingTimeDistributionEnum,
  FixedAmountStrategyFragment,
  VariableAmountStrategyFragment,
} from 'generated/resources/graphql'
import { ContractsBankHolidayTreatmentEnum } from 'generated/resources/graphql'

import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import { weekdaysWithUserFirstDayOfWeek } from 'modules/core/lib/firstDayOfWeek'
import centsToDecimal from 'modules/core/lib/formatCents'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'
import type { Collection, Model } from 'modules/core/lib/mobx-rest'
import users from 'modules/core/store/collections/users'

import type { Update } from 'modules/contracts/components/ContractHistory/ContractVersionUpdate'
import { additionalCompensationType } from 'modules/contracts/lib/additionalCompensations'
import { contractVersionEmployee } from 'modules/contracts/lib/contractVersionEmployee'
import { formatCustomFieldValue } from 'modules/contracts/lib/formatCustomFieldValue'
import type { LegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import { translatedWeekdays } from 'modules/contracts/lib/weekdays'
import Compensation from 'modules/contracts/store/models/Compensation'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import employees from 'modules/employees/store/collections/employees'
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import { jobLevelRole } from 'modules/job_catalog/store/collections/roles'

import type ContractVersionRequest from '../store/models/ContractVersionRequest'
import { ContractFieldsProps } from './types'

const SALARY_KEYS = ['salary_amount', 'salary_frequency']
const WORKING_HOURS_KEYS = ['working_hours', 'working_hours_frequency']
const NO_DETAILED_KEYS = ['es_contract_observations', 'es_job_description']
const BOOLEAN_KEYS = ['es_has_teleworking_contract', 'has_trial_period']
const JOB_TITLE_KEYS = ['job_title', 'job_catalog_level_id']

const NONE = () => i18n.t('contract_versions.navigator.changes.none')

const getSalaryFrequency = (salaryFrequency: string | undefined) => {
  switch (salaryFrequency) {
    case 'yearly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.yearly')
    case 'monthly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.monthly')
    case 'weekly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.weekly')
    case 'daily':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.daily')
    case 'hourly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.hourly')
  }
}

const formatBankHolidayTreatment = (bankHolidayTreatment: ContractsBankHolidayTreatmentEnum) => {
  switch (bankHolidayTreatment) {
    case 'workable':
      return i18n.t('contract_versions.time.bank_holidays.working_time')
    case 'non_workable':
      return i18n.t('contract_versions.time.bank_holidays.non_working_time')
    default:
      const exhaustiveCheck: never = bankHolidayTreatment
      throw new Error(`Unhandled bankHolidayTreatment: ${exhaustiveCheck}`)
  }
}

const formatAnnualWorkingTimeDistribution = (
  annualWorkingTimeDistribution: ContractsAnnualWorkingTimeDistributionEnum
) => {
  switch (annualWorkingTimeDistribution) {
    case 'limit_workdays':
      return i18n.t('contract_versions.time.annual_working_time_distribution.limit_workdays')
    case 'limit_daily_hours':
      return i18n.t('contract_versions.time.annual_working_time_distribution.limit_daily_hours')
    default:
      const exhaustiveCheck: never = annualWorkingTimeDistribution
      throw new Error(`Unhandled annualWorkingTimeDistribution: ${exhaustiveCheck}`)
  }
}

const formatContractHours = (contractVersion?: ContractVersion | ContractVersionRequest) => {
  if (!contractVersion) return undefined
  const amount = centsToDecimal(contractVersion.attributes.working_hours)
  const frequency = contractVersion.attributes.working_hours_frequency || ''

  if (amount && frequency) {
    return i18n.t('contract_versions.navigator.changes.working_schedule', {
      amount: amount,
      frequency: ContractVersion.WorkingHoursFrequency()[frequency] || '',
    })
  }
}

const formatSalary = (contractVersion?: ContractVersion | ContractVersionRequest) => {
  if (!contractVersion) return undefined
  const frequency = contractVersion.attributes.salary_frequency || ''
  const cvEmployee = contractVersionEmployee(contractVersion, employees)
  const currency = formatCurrency(
    contractVersion.attributes.salary_amount,
    undefined,
    null,
    cvEmployee && employeeLegalEntityId(cvEmployee)
  )

  if (currency && frequency) return `${currency} ${getSalaryFrequency(frequency)}`
  return null
}

const formatWeekDays = (workingDays: string) => {
  if (!workingDays) return NONE()
  const currentUser = users.current
  const weekDays = workingDays.split(',')
  if (weekDays.length == 0) {
    return NONE()
  }

  const orderedWeekDays = weekdaysWithUserFirstDayOfWeek(
    currentUser.attributes.first_day_of_week
  ).filter((item) => weekDays.includes(item.toLowerCase()))

  const translatedDays = orderedWeekDays.map((day) => translatedWeekdays(day))

  return translatedDays.join(', ')
}

const formatJobTitle = (contractVersion?: ContractVersion | ContractVersionRequest) => {
  if (!contractVersion) return undefined
  const jobTitle = contractVersion.attributes.job_title
  const jobCatalogLevel = jobLevelsCollection.get(contractVersion.attributes.job_catalog_level_id)
  const jobCatalogRole = jobCatalogLevel ? jobLevelRole(jobCatalogLevel) : null

  const result: string[] = []

  if (jobTitle) {
    result.push(jobTitle)
  }

  const jobRole = jobCatalogRole?.attributes.name
  if (jobRole) {
    result.push(jobRole)
  }

  const jobLevel = jobCatalogLevel?.attributes.is_default ? '' : jobCatalogLevel?.attributes.name
  if (jobLevel) {
    result.push(jobLevel)
  }

  return result.join(', ')
}

const formatCollection = (
  options: Collection<Model>,
  key: string,
  currentValue: number,
  previousValue?: number
): Update => {
  return {
    key: key,
    currentValue: options.get(currentValue)?.attributes.name || NONE(),
    previousValue: previousValue
      ? options.get(previousValue)?.attributes.name || NONE()
      : undefined,
  }
}

const formatField = (
  options: ContractFieldsProps[],
  key: string,
  currentValue: string | number | undefined,
  previousValue: string | number | undefined
): Update => {
  return {
    key: key,
    currentValue: options.find(({ id }) => id === currentValue)?.name || NONE(),
    previousValue: previousValue
      ? options.find(({ id }) => id === previousValue)?.name || NONE()
      : undefined,
  }
}

interface Nameable {
  id?: number
  code?: number
  name: string
}

const formatNameable = (
  options: Nameable[],
  key: string,
  currentValue: number,
  previousValue?: number
): Update => {
  const currentOptionName = options.find(
    ({ id, code }) => id === currentValue || code === currentValue
  )?.name
  const previousOptionName = previousValue
    ? options.find(({ id, code }) => id === previousValue || code === previousValue)?.name || NONE()
    : undefined

  return {
    key: key,
    currentValue: currentOptionName || NONE(),
    previousValue: previousOptionName,
  }
}

const formatBooleanKey = (
  key: string,
  contractVersion?: ContractVersion | ContractVersionRequest
) => {
  if (!contractVersion) return undefined
  if (key in contractVersion.attributes) {
    return contractVersion.attributes[key as keyof typeof contractVersion.attributes]
      ? i18n.t('contract_versions.navigator.changes.yes')
      : i18n.t('contract_versions.navigator.changes.no')
  }
  return undefined
}

const formatUpdate = (
  key: string,
  legalEntityContractData: LegalEntityContractData,
  currentVersion: ContractVersion | ContractVersionRequest,
  previousVersion?: ContractVersion
): Update => {
  if (SALARY_KEYS.includes(key)) {
    return {
      key: 'gross_salary',
      currentValue: formatSalary(currentVersion),
      previousValue: formatSalary(previousVersion),
    }
  }

  if (WORKING_HOURS_KEYS.includes(key)) {
    return {
      key: 'contract_hours',
      currentValue: formatContractHours(currentVersion),
      previousValue: formatContractHours(previousVersion),
    }
  }

  if (NO_DETAILED_KEYS.includes(key)) {
    return {
      key: key,
      currentValue: null,
      previousValue: null,
    }
  }

  if (BOOLEAN_KEYS.includes(key)) {
    return {
      key: key,
      currentValue: formatBooleanKey(key, currentVersion),
      previousValue: formatBooleanKey(key, previousVersion),
    }
  }

  if (JOB_TITLE_KEYS.includes(key)) {
    return {
      key: 'job_title',
      currentValue: formatJobTitle(currentVersion),
      previousValue: formatJobTitle(previousVersion),
    }
  }

  switch (key) {
    case 'es_working_day_type_id':
      return formatField(
        legalEntityContractData.spanishContractData.workingDayTypes,
        key,
        currentVersion.attributes.es_working_day_type_id,
        previousVersion?.attributes?.es_working_day_type_id || undefined
      )
    case 'working_week_days':
      return {
        key: key,
        currentValue: formatWeekDays(currentVersion.attributes.working_week_days),
        previousValue: formatWeekDays(previousVersion?.attributes?.working_week_days || ''),
      }
    case 'max_legal_yearly_hours':
      return {
        key: key,
        currentValue: centsToDecimal(currentVersion.attributes.max_legal_yearly_hours),
        previousValue: centsToDecimal(previousVersion?.attributes?.max_legal_yearly_hours || 0),
      }
    case 'maximum_weekly_hours':
      return {
        key: key,
        currentValue: centsToDecimal(currentVersion.attributes.maximum_weekly_hours),
        previousValue: centsToDecimal(previousVersion?.attributes?.maximum_weekly_hours || 0),
      }
    case 'es_contract_type_id':
      return formatField(
        legalEntityContractData.spanishContractData.contractTypes,
        key,
        currentVersion.attributes.es_contract_type_id,
        previousVersion?.attributes?.es_contract_type_id || undefined
      )
    case 'es_education_level_id':
      return formatField(
        legalEntityContractData.spanishContractData.educationLevels,
        key,
        currentVersion.attributes.es_education_level_id,
        previousVersion?.attributes?.es_education_level_id || undefined
      )
    case 'es_regime_id':
      return formatCollection(
        legalEntityContractData.spanishContractData.regimes,
        key,
        currentVersion.attributes.es_regime_id,
        previousVersion?.attributes?.es_regime_id || undefined
      )
    case 'es_cotization_group':
      return formatNameable(
        legalEntityContractData.spanishContractData.cotizationGroup,
        key,
        currentVersion.attributes.es_cotization_group,
        previousVersion?.attributes?.es_cotization_group || undefined
      )
    case 'es_cno_occupation_id':
      return formatCollection(
        legalEntityContractData.spanishContractData.cnoOccupations,
        key,
        currentVersion.attributes.es_cno_occupation_id,
        previousVersion?.attributes?.es_cno_occupation_id || undefined
      )
    case 'es_contribution_type_id':
      return formatCollection(
        legalEntityContractData.spanishContractData.contributionTypes,
        key,
        currentVersion.attributes.es_contribution_type_id,
        previousVersion?.attributes?.es_contribution_type_id || undefined
      )
    case 'es_agreement_code_id':
      return formatCollection(
        legalEntityContractData.spanishContractData.agreementCodes,
        key,
        currentVersion.attributes.es_agreement_code_id,
        previousVersion?.attributes?.es_agreement_code_id || undefined
      )
    case 'es_occupation_code_id':
      return formatCollection(
        legalEntityContractData.spanishContractData.occupationCodes,
        key,
        currentVersion.attributes.es_occupation_code_id,
        previousVersion?.attributes?.es_occupation_code_id || undefined
      )
    case 'es_a3innuva_job_position_id':
      return formatCollection(
        legalEntityContractData.spanishContractData.a3innuvaJobPositions,
        key,
        currentVersion.attributes.es_a3innuva_job_position_id,
        previousVersion?.attributes?.es_a3innuva_job_position_id || undefined
      )
    case 'es_classification_id':
      return formatCollection(
        legalEntityContractData.spanishContractData.classifications,
        key,
        currentVersion.attributes.es_classification_id,
        previousVersion?.attributes?.es_classification_id || undefined
      )
    case 'es_professional_category_id':
      return formatNameable(
        legalEntityContractData.spanishContractData.professionalCategories,
        key,
        currentVersion.attributes.es_professional_category_id,
        previousVersion?.attributes?.es_professional_category_id || undefined
      )
    case 'fr_contract_type_id':
      return formatField(
        legalEntityContractData.frenchContractData.contractTypes,
        key,
        currentVersion.attributes.fr_contract_type_id,
        previousVersion?.attributes?.fr_contract_type_id || undefined
      )
    case 'fr_forfait_jours':
      const forfaitJours = legalEntityContractData.frenchContractData.forfaitJours
      return {
        key: key,
        currentValue:
          forfaitJours.find((group) => group.value === currentVersion.attributes.fr_forfait_jours)
            ?.label || NONE(),
        previousValue:
          forfaitJours.find(
            (group) => group.value === previousVersion?.attributes?.fr_forfait_jours
          )?.label || NONE(),
      }
    case 'fr_jours_par_an_cents':
      return {
        key: 'fr_jours_par_an',
        currentValue: centsToDecimal(currentVersion.attributes.fr_jours_par_an_cents),
        previousValue: centsToDecimal(previousVersion?.attributes?.fr_jours_par_an_cents || 0),
      }
    case 'fr_level_id':
      return formatField(
        legalEntityContractData.frenchContractData.levels,
        key,
        currentVersion.attributes.fr_level_id,
        previousVersion?.attributes?.fr_level_id || undefined
      )
    case 'fr_mutual_id':
      return formatField(
        legalEntityContractData.frenchContractData.mutuals,
        key,
        currentVersion.attributes.fr_mutual_id,
        previousVersion?.attributes?.fr_mutual_id || undefined
      )
    case 'fr_professional_category_id':
      return formatField(
        legalEntityContractData.frenchContractData.professionalCategories,
        key,
        currentVersion.attributes.fr_professional_category_id,
        previousVersion?.attributes?.fr_professional_category_id || undefined
      )
    case 'fr_step_id':
      return formatField(
        legalEntityContractData.frenchContractData.steps,
        key,
        currentVersion.attributes.fr_step_id,
        previousVersion?.attributes?.fr_step_id || undefined
      )
    case 'fr_work_type_id':
      return formatField(
        legalEntityContractData.frenchContractData.workTypes,
        key,
        currentVersion.attributes.fr_work_type_id,
        previousVersion?.attributes?.fr_work_type_id || undefined
      )
    case 'de_contract_type_id':
      return formatField(
        legalEntityContractData.germanContractData.contractTypes,
        key,
        currentVersion.attributes.de_contract_type_id,
        previousVersion?.attributes?.de_contract_type_id || undefined
      )
    case 'pt_contract_type_id':
      return formatField(
        legalEntityContractData.portugueseContractData.contractTypes,
        key,
        currentVersion.attributes.pt_contract_type_id,
        previousVersion?.attributes?.pt_contract_type_id || undefined
      )
    case 'working_time_percentage_in_cents':
      return {
        key: key,
        currentValue: currentVersion.attributes.working_time_percentage_in_cents
          ? centsToDecimal(currentVersion.attributes.working_time_percentage_in_cents)
          : NONE(),
        previousValue: previousVersion?.attributes?.working_time_percentage_in_cents
          ? centsToDecimal(previousVersion?.attributes?.working_time_percentage_in_cents || 0)
          : NONE(),
      }
    case 'bank_holiday_treatment':
      return {
        key: key,
        currentValue: formatBankHolidayTreatment(currentVersion.attributes.bank_holiday_treatment),
        previousValue: previousVersion?.attributes?.bank_holiday_treatment
          ? formatBankHolidayTreatment(previousVersion?.attributes?.bank_holiday_treatment)
          : NONE(),
      }
    case 'annual_working_time_distribution':
      return {
        key: key,
        currentValue: formatAnnualWorkingTimeDistribution(
          currentVersion.attributes.annual_working_time_distribution
        ),
        previousValue: previousVersion?.attributes?.annual_working_time_distribution
          ? formatAnnualWorkingTimeDistribution(
              previousVersion?.attributes?.annual_working_time_distribution
            )
          : NONE(),
      }
    default:
      if (key in currentVersion.attributes) {
        const currentValue =
          currentVersion.attributes[key as keyof typeof currentVersion.attributes]
        let previousValue =
          previousVersion?.attributes[key as keyof typeof previousVersion.attributes]
        if (previousValue === true) {
          previousValue = i18n.t('contract_versions.navigator.changes.yes')
        } else if (previousValue === false) {
          previousValue = i18n.t('contract_versions.navigator.changes.no')
        }

        return {
          key: key,
          currentValue: currentValue || NONE(),
          previousValue: previousValue || NONE(),
        }
      }
      return {
        key: key,
        currentValue: NONE(),
        previousValue: NONE(),
      }
  }
}

export const formatAdditionalCompensationUpdate = (
  key: string,
  compensation: Compensation,
  contractVersion: ContractVersion | ContractVersionRequest,
  locale: string,
  taxonomy?: ContractTaxonomyFragment
): Update => {
  const amount = Compensation.GetUnitValue({
    compensation,
    employee: contractVersionEmployee(contractVersion, employees),
    locale,
  })

  const compensationType = additionalCompensationType(compensation.attributes.compensation_type)

  if (key === 'deleted_additional_compensation')
    return {
      key: key,
      previousValue: i18n.t('contract_versions.navigator.changes.additional_compensation', {
        amount: amount,
        compensationType: compensationType,
        taxonomy: taxonomy?.name,
      }),
      currentValue: i18n.t('contract_versions.navigator.changes.removed'),
    }

  return {
    key: key,
    currentValue: i18n.t('contract_versions.navigator.changes.additional_compensation', {
      amount: amount,
      compensationType: compensationType,
      taxonomy: taxonomy?.name,
    }),
  }
}

const isFixedAmountStrategyCell = (
  amountStrategy: AmountStrategyHistoryFragment
): amountStrategy is FixedAmountStrategyFragment =>
  amountStrategy.__typename === 'ContractsFixedAmountStrategy'
const isVariableAmountStrategyCell = (
  amountStrategy: AmountStrategyHistoryFragment
): amountStrategy is VariableAmountStrategyFragment =>
  amountStrategy.__typename === 'ContractsVariableAmountStrategy'
const isNotLimit = (amountStrategy: VariableAmountStrategyFragment) =>
  amountStrategy?.upperLimit === undefined || amountStrategy?.upperLimit === null

export const formatNewAdditionalCompensationUpdate = (
  key: string,
  compensation: AdditionalCompensationsContractsHistoryFragment
): Update => {
  let compensationUpdatedValue: string | null = null

  if (compensation.amountStrategy && isFixedAmountStrategyCell(compensation.amountStrategy)) {
    compensationUpdatedValue = i18n.t(
      'contract_versions.navigator.changes.new_additional_compensation_fixed',
      {
        amount: compensation.amountStrategy.amount,
        compensationType: i18n.t('contract_versions.additional_compensation.history.fixed_label'),
        payrollConcept: compensation.payrollConcept?.name,
      }
    )
  } else if (
    compensation.amountStrategy &&
    isVariableAmountStrategyCell(compensation.amountStrategy) &&
    isNotLimit(compensation.amountStrategy)
  ) {
    compensationUpdatedValue = i18n.t(
      'contract_versions.navigator.changes.new_additional_compensation_variable_no_limit',
      {
        amount: compensation.amountStrategy.amount,
        compensationType: i18n.t(
          'contract_versions.additional_compensation.history.variable_label'
        ),
        payrollConcept: compensation.payrollConcept?.label,
      }
    )
  } else if (
    compensation.amountStrategy &&
    isVariableAmountStrategyCell(compensation.amountStrategy)
  ) {
    compensationUpdatedValue = i18n.t(
      'contract_versions.navigator.changes.new_additional_compensation_variable_up_to',
      {
        amount: compensation.amountStrategy.upperLimit,
        compensationType: i18n.t(
          'contract_versions.additional_compensation.history.variable_label'
        ),
        payrollConcept: compensation.payrollConcept?.label,
      }
    )
  }
  if (key === 'deleted_additional_compensation') {
    return {
      key: key,
      previousValue: compensationUpdatedValue,
      currentValue: i18n.t('contract_versions.navigator.changes.removed'),
    }
  }

  return {
    key: key,
    currentValue: compensationUpdatedValue,
  }
}

export const formatCustomFieldUpdate = (
  customFieldKey: string,
  currentCustomFieldValue?: CustomFieldValue,
  previousCustomFieldValue?: CustomFieldValue
): Update => {
  if (!currentCustomFieldValue) {
    return {
      key: i18n.t('contract_versions.navigator.changes.deleted_custom_field', {
        name: previousCustomFieldValue?.attributes.label || '',
      }),
      previousValue: formatCustomFieldValue(previousCustomFieldValue),
      currentValue: i18n.t('contract_versions.navigator.changes.removed'),
    }
  }

  if (!previousCustomFieldValue) {
    return {
      key: customFieldKey,
      currentValue: `${currentCustomFieldValue.attributes.label}, ${formatCustomFieldValue(
        currentCustomFieldValue
      )}`,
    }
  }
  return {
    key: i18n.t('contract_versions.navigator.changes.updated_custom_field', {
      name: currentCustomFieldValue.attributes.label,
    }),
    currentValue: formatCustomFieldValue(currentCustomFieldValue),
    previousValue: formatCustomFieldValue(previousCustomFieldValue),
  }
}

export default formatUpdate
